// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Contenu intégral reproduit à partir des documents juridiques officiels
// (CGU/CGV v1.1, Annexe A — Avertissement IA v1.1, Politique de Confidentialité
// v1.2, Mentions Légales v1.1, SLA v1.2 — 01/06/2026, SLA applicable 01/10/2026).
// Ne pas modifier le fond de ces textes sans mise à jour préalable des documents
// sources (dossier "Juridiques et Contractuels").
//
// ⚠️ 08/09/2026 — grille à trois paliers (Essentiel / Pro / Cabinet), passée sur
// l'INTÉGRALITÉ des documents à la demande de Marc, qui met à jour les PDF
// sources en parallèle. Onze endroits portaient PRO/PRO+ : CGU/CGV art. 2.3
// (suspension), 3.1 (grille), 4.2 (facturation), 7 (conservation) ;
// Confidentialité § 12.4 ; SLA art. 3.1, 3.2, 6.2 et 10.
// ⛔ Tant que les PDF ne sont pas republiés, le site est EN AVANCE sur le
// contrat signé : c'est voulu et temporaire, pas un état stable.
//
// ⚠️ 24/09/2026 — CGU/CGV 1.3, Confidentialité 1.3, Mentions légales 1.2, en
// vigueur immédiatement (modifications non substantielles au sens de l'art. 11
// des CGV : précisions et mise en conformité avec l'existant). À reporter dans
// les documents sources :
//   - CGV art. 1 : le Client personne physique « agissant à titre professionnel » ;
//   - CGV art. 4.2 : obligation de fournir SIREN, dénomination et adresse de
//     facturation exacts, dont dépend la facture électronique
//     (docs/contexte/etude-facturation-electronique-2026-09.md) ;
//   - STRIPE ajouté aux prestataires (CGV art. 6, Confidentialité § 3, Mentions
//     § 4) : il encaisse les abonnements depuis juillet 2026 et n'y figurait
//     nulle part. La phrase « exclusivement des prestataires français, aucun
//     transfert hors UE » devenait fausse : réécrite pour ne viser que les
//     rapports, qui ne quittent jamais Mistral AI et OVH ;
//   - Confidentialité § 2.1 : « SIRET » ➜ SIREN, dénomination, adresse de facturation.
//
// ⚠️ 24/09/2026 (après-midi) — mise en conformité avec l'EXISTANT, dans les
// versions en vigueur (décision de Marc, comme l'art. 11 des CGV) :
//   - CONSERVATION : les textes promettaient « 6 mois » pour les rapports ET les
//     analyses, une prolongation payante (0,002 €/mois/rapport) et une
//     suppression 90 jours après résiliation. Réalité : les PDF déposés sont
//     purgés à 180 jours (`scripts/cleanup_uploads.sh`), les résultats ne le
//     sont jamais — ils vivent autant que le compte —, la prolongation payante
//     n'a jamais existé, et rien ne supprime un compte à la résiliation. ⛔ Ne
//     jamais changer l'une de ces durées sans le script, ni l'inverse ;
//   - aucune API n'existe : retirée des CGV (préambule, art. 1) et du SLA ;
//   - pénalités de retard : « taux légal + 5 points » était sous le minimum de
//     l'art. L. 441-10 du Code de commerce (trois fois le taux légal) ;
//   - CGV art. 8.2 : « conseil par IA » ➜ l'activité réellement assurée ;
//   - Confidentialité § 3 : « OVH certifié HDS » retiré (non vérifié pour un VPS).
//
// ⚠️ 28/09/2026 — CGU/CGV 1.4 (grille 59 / 119 / 449 €, art. 3.5 « Abonnements
// multiples », relu par une IA juridique) et Confidentialité 1.4, toutes deux au
// 29/09/2026. Confidentialité § 2.1 : nouvelle finalité, le rapprochement SIREN /
// domaine de messagerie des abonnés (`core/plafond_sieges.py`), fondé sur
// l'intérêt légitime. Docx sources refaits le même jour.
//
// ⚠️ 30/09/2026 — Confidentialité, réécrit DANS la version 1.4, sans nouvelle
// version (décision de Marc : aucun client n'utilise encore le logiciel, les
// règles de date et d'avertissement ne concernent personne). Finalité de
// l'analyse : « production du résultat de conformité » ➜ « production d'une
// extraction au format xls et d'un rapport d'analyse en format pdf » — la
// phrase du docx source, que Marc a mis à jour lui-même.
import React from 'react';
import { Link } from 'wouter';
import { Article, Sub, List, DataTable, Callout, EnTeteDocument } from '../components/DocLayout';
import { useI18n } from '../i18n';
import { getCgvVersions } from '../services/api';

const PAGES_META = {
  // ⚠️ CGV, Privacy et SLA sont passés en 1.2 le 08/09/2026 (grille à trois
  // paliers). Le 24/09/2026 : CGV et Privacy en 1.3, Mentions légales en 1.2
  // (identité de facturation, Stripe ajouté aux prestataires), SLA en 1.3 (son
  // introduction citait encore « Pro, Pro+ », oubli du 08/09). Avertissement IA
  // (1.1) n'a pas bougé — incrémenter une version sans
  // changement de fond ferait croire à une révision qui n'a pas eu lieu.
  // ⚠️ Les CGV n'ont PLUS de version ni de date écrites ici (24/09/2026) : elles
  // se lisent dans `CGV_VERSIONS` (plus bas), miroir du calendrier de
  // `core/cgv.py`. Une chaîne recopiée à la main est exactement ce qui avait
  // divergé le 08/09 (page en 1.2, serveur resté en 1.1).
  cgv: {
    title: "Conditions Générales d'Utilisation et de Vente — CGU/CGV",
  },
  'avertissement-ia': {
    title: "Annexe A — Avertissement relatif aux rapports d'analyse par intelligence artificielle",
    version: 'Version 1.1',
    date: 'Entrée en vigueur : 01/06/2026',
  },
  privacy: {
    title: 'Politique de Confidentialité et Protection des Données (RGPD)',
    version: 'Version 1.4',
    date: 'Applicable à partir du 29/09/2026',
  },
  mentions: {
    title: 'Mentions Légales',
    version: 'Version 1.2',
    date: 'En vigueur à compter du 24/09/2026',
  },
  // ⚠️ SLA 1.4 (décision de Marc, 24/09/2026) : disponibilité 99 % pour les
  // abonnements, 99,5 % au plus pour Entreprise / Grand Compte — un seul VPS ne
  // tient pas 99,9 % (43 min d'arrêt par mois). Révision DÉFAVORABLE au client :
  // l'art. 11.1 impose 30 jours de préavis, d'où l'application au 24/10/2026 et
  // la phrase de l'introduction qui maintient la 1.3 jusque-là.
  sla: {
    title: 'Contrat de Maintenance et de Support — SLA',
    version: 'Version 1.4',
    date: 'Applicable à partir du 24/10/2026',
  },
};

const CROSS_LINKS = [
  { href: '/cgv', label: 'CGU/CGV' },
  { href: '/avertissement-ia', label: 'Avertissement IA' },
  { href: '/privacy', label: 'Confidentialité' },
  { href: '/mentions-legales', label: 'Mentions légales' },
  { href: '/sla', label: 'SLA' },
];

// ── Mise en page ───────────────────────────────────────────────────────────
// Article, Sub, List, DataTable et Callout ont été sortis dans
// `components/DocLayout.js` le 31/08/2026, à l'arrivée de la page d'aide :
// `/aide` reformule en langage métier un dispatch qui est contractuel (SLA
// art. 8), et les deux pages doivent se lire comme un seul document.
// ⛔ Aucun texte juridique n'a bougé — que la déclaration des composants.

// ── CGU/CGV ───────────────────────────────────────────────────────────────

// Version 1.4 (décisions tarifaires de Marc du 28/09/2026) : grille
// 59 / 119 / 449 €, Cabinet à 150 analyses et 300 au plus par mois, pool annuel
// des licences Entreprise, article 3.5 contre le fractionnement en abonnements.
function CGVContent() {
  return (
    <>
      <Callout tone="blue">
        <p><strong>EVALENS</strong> (ci-après « le Prestataire ») est une SASU de droit français dont le siège
        social est situé au 58 rue de Monceau CS 48756 75380 Paris Cedex 08, immatriculée au RCS de PARIS sous
        le numéro 103593489.</p>
        <p className="mt-2">Normascan est un service logiciel en mode SaaS (Software as a Service) permettant
        l'analyse automatisée de rapports techniques normatifs, notamment dans le domaine du diagnostic amiante
        (norme NF X46-020) et autres normes BTP, par le biais de technologies d'intelligence artificielle.</p>
        <p className="mt-2">Les présentes Conditions Générales d'Utilisation et de Vente (ci-après « CGU/CGV »)
        régissent l'accès et l'utilisation de Normascan. En accédant au service, tout Utilisateur ou Client
        accepte sans réserve les présentes CGU/CGV.</p>
        <p className="mt-2"><strong>Normascan est un outil d'aide à la décision.</strong> Les résultats fournis
        ne constituent pas un avis juridique et ne sauraient remplacer l'analyse d'un professionnel certifié.
        La décision finale reste toujours de la responsabilité de l'utilisateur professionnel.</p>
        <p className="mt-2">Le présent contrat se compose des présentes CGU/CGV, du document <strong>Annexe A —
        Avertissement relatif aux rapports d'analyse par intelligence artificielle</strong> qui définit les
        conditions d'exploitation des rapports, ainsi que, le cas échéant, du SLA applicable. L'Annexe A est
        accessible à l'adresse <Link href="/avertissement-ia" className="underline">app.normascan.fr/avertissement-ia</Link> et
        fait partie intégrante du présent contrat.</p>
      </Callout>

      <Article n={1} title="Définitions">
        <List items={[
          <><strong>Service / Normascan</strong> : la plateforme logicielle accessible via https://app.normascan.fr.</>,
          <><strong>Utilisateur</strong> : toute personne physique accédant au Service dans le cadre d'un compte professionnel.</>,
          <><strong>Client</strong> : la personne morale, ou la personne physique agissant à titre professionnel, ayant souscrit à une licence Normascan et responsable du paiement.</>,
          <><strong>Compte</strong> : l'espace personnel créé sur la plateforme permettant l'accès aux fonctionnalités selon le tier souscrit.</>,
          <><strong>Analyse LLM</strong> : le traitement automatisé d'un rapport par le modèle d'intelligence artificielle Mistral Large.</>,
          <><strong>Pool d'analyses</strong> : nombre d'analyses incluses dans un abonnement Cabinet (renouvelé chaque mois) ou dans une licence Entreprise (renouvelé à chaque date anniversaire du contrat), partagé entre ses Utilisateurs.</>,
          <><strong>OCR</strong> : la conversion optique de caractères (PDF scannés → texte) réalisée par Mistral OCR.</>,
          <><strong>Rapport</strong> : tout document PDF uploadé sur la plateforme pour analyse.</>,
          <><strong>Résultat d'analyse</strong> : la sortie produite par Normascan à l'issue du traitement d'un rapport.</>,
          <><strong>Technologies tierces</strong> : l'API Mistral AI (Mistral AI SAS, Paris, France), l'hébergement OVH SAS, et tout autre prestataire technologique mentionné dans la Politique de Confidentialité.</>,
        ]} />
      </Article>

      <Article n={2} title="Accès au service et création de compte">
        <Sub title="2.1 Conditions d'accès">
          <p>L'accès à Normascan est dédié aux professionnels (B2B). En s'inscrivant, l'Utilisateur déclare :</p>
          <List items={[
            "Être majeur et avoir la capacité juridique de contracter",
            "Agir dans un cadre professionnel ou pour le compte d'une personne morale",
            "Fournir des informations exactes et à jour lors de la création du compte",
          ]} />
        </Sub>
        <Sub title="2.2 Inscription et compte">
          <p>L'Utilisateur crée son compte via app.normascan.fr/signup ou sur invitation reçue par mail depuis
          contact@normascan.fr. Il est responsable de la confidentialité de ses identifiants. Tout accès avec ses
          identifiants est réputé effectué par lui. En cas de compromission, l'Utilisateur doit notifier
          immédiatement Evalens à support@normascan.fr.</p>
        </Sub>
        <Sub title="2.3 Suspension et résiliation de compte">
          <p>Evalens se réserve le droit de suspendre ou résilier un compte en cas de :</p>
          <List items={[
            'Violation des présentes CGU/CGV',
            'Non-paiement de la licence après mise en demeure',
            'Non-paiement ou rejet de paiement d\'un abonnement Essentiel, Pro ou Cabinet, ou d\'une licence Entreprise',
            'Utilisation frauduleuse ou contraire à la loi',
            'Tentative de contournement des mesures de sécurité',
          ]} />
        </Sub>
      </Article>

      <Article n={3} title="Licences et offres commerciales">
        <Sub title="3.1 Abonnements self-service (résiliables à tout moment)">
          <p>Normascan est disponible sous trois formules, en paiement mensuel, sans engagement de durée :</p>
          <DataTable
            headers={['Offre', 'Prix HT', 'Utilisateurs', 'Analyses incluses / mois', 'Analyse supplémentaire', 'Mode Projet', 'OCR', 'Vérification des certifications', 'Engagement']}
            // « Vérification des certifications » : Pro et Cabinet uniquement —
            // c'est `PALIERS_VERIFICATION_CERTIFICATION` (core/droits.py).
            rows={[
              ['Essentiel', '59 €/mois', '1', '10', '3,90 € HT', 'Non inclus', 'Inclus', 'Non incluse', 'Sans engagement, résiliable à tout moment'],
              ['Pro', '119 €/mois', '1', '30', '2,90 € HT', 'Inclus', 'Inclus', 'Incluse', 'Sans engagement, résiliable à tout moment'],
              ['Cabinet', '449 €/mois', '5', '150, partagées entre les sièges', '2,90 € HT', 'Inclus', 'Inclus', 'Incluse', 'Sans engagement, résiliable à tout moment'],
            ]}
          />
          <p className="text-xs text-gray-500">Les analyses incluses se renouvellent chaque mois et ne se
          reportent pas. Au-delà du forfait, chaque analyse supplémentaire est facturée au tarif ci-dessus.
          L'OCR des documents scannés est compris dans les trois formules, sans supplément. Un abonnement
          Cabinet permet au plus 300 analyses par mois, forfait compris ; au-delà, une licence Entreprise est
          proposée. L'organisation des Utilisateurs en agences ou échelons et les plafonds d'analyses par
          Utilisateur sont réservés aux licences Entreprise.</p>
        </Sub>
        <Sub title="3.2 Licences SaaS personnalisées (engagement annuel)">
          <p>Normascan est disponible sous licence logiciel SaaS. Les caractéristiques de chaque licence (nombre
          d'utilisateurs, pool d'analyses, pool OCR, setup, modules disponibles, SLA) sont définies sur devis.</p>
        </Sub>
        <Sub title="3.3 Limites contractuelles">
          <p><strong>Chaque abonnement ou licence Entreprise inclut des limites strictement contractuelles</strong> (nombre
          d'utilisateurs, volume mensuel ou annuel d'analyses LLM, pool OCR, nombre de domaines/normes). Ces limites sont
          expressément acceptées à la signature du contrat.</p>
          <p>Tout dépassement de ces limites donne lieu à une facturation automatique selon les tarifs sur contrat.</p>
        </Sub>
        <Sub title="3.4 Engagement annuel (licences Entreprise et Grand Compte uniquement)">
          <p>Les licences SaaS personnalisées visées à l'article 3.2 sont souscrites pour une durée minimale
          d'un an, renouvelable par tacite reconduction. Cet engagement annuel n'est pas résiliable par
          anticipation sauf accord exprès d'Evalens ou motif légitime (non-respect répété du SLA sur 3 mois
          consécutifs).</p>
          <p>Les abonnements self-service Essentiel, Pro et Cabinet visés à l'article 3.1 ne comportent
          <strong> aucun engagement de durée</strong> : ils sont mensuels et résiliables à tout moment, la
          résiliation prenant effet au terme du mois en cours.</p>
        </Sub>
        <Sub title="3.5 Abonnements multiples">
          {/* Réécrit le 28/09/2026 après relecture juridique : le SIREN seul bloque
              (`core/plafond_sieges.py`), un domaine partagé ne fait qu'alerter Evalens ;
              procédure contradictoire, grille communiquée avec la notification (les
              prix Entreprise ne sont pas publiés), rétroactivité plafonnée à 12 mois. */}
          <p>Un même Client, identifié par son numéro SIREN, ne peut réunir plus de cinq (5) sièges payants en
          abonnements self-service, ni plus d'un abonnement Cabinet. Au-delà, Normascan est proposé
          exclusivement en licence Entreprise. Une souscription qui porterait ce nombre au-delà de cinq sièges
          pour un même SIREN est refusée, et le Client est invité à contacter Evalens.</p>
          <p>Des comptes partageant un même domaine de messagerie professionnelle peuvent faire l'objet d'une
          vérification. Un domaine commun ne suffit pas, à lui seul, à établir qu'ils relèvent d'un même
          Client.</p>
          <p>Si Evalens constate que des abonnements self-service souscrits pour le compte d'une même personne
          morale dépassent cinq sièges, y compris sous d'autres comptes ou d'autres adresses de messagerie,
          elle le notifie au Client par écrit. La notification indique les comptes concernés et les éléments
          qui les rattachent. Elle est accompagnée de la grille tarifaire de la licence Entreprise en vigueur à
          la date du constat et du détail du calcul de la somme due. Cette grille est par ailleurs communiquée à
          tout Client qui en fait la demande.</p>
          <p>Le Client dispose de trente (30) jours à compter de la notification pour présenter ses observations
          ou régulariser sa situation, en résiliant les abonnements excédentaires ou en souscrivant une licence
          Entreprise. En cas de résiliation des abonnements excédentaires dans ce délai, la résiliation prend
          effet immédiatement, par dérogation à l'article 3.4, et Evalens peut, à la demande du Client, lui
          rembourser la part de l'abonnement mensuel restant à courir, au prorata. À défaut, Evalens peut requalifier ces abonnements en licence Entreprise. Elle facture
          alors la différence entre les sommes versées au titre de ces abonnements et le prix, selon cette
          grille, de la formule de licence Entreprise la moins chère couvrant le nombre de sièges constaté.
          Cette différence est calculée au prorata de la période concernée, dans la limite des douze (12) mois
          précédant la notification.</p>
        </Sub>
      </Article>

      <Article n={4} title="Prix, facturation et paiement">
        <Sub title="4.1 Prix">
          <p>Les prix sont exprimés en euros HT. La TVA applicable au taux en vigueur est ajoutée à la facturation.
          Les prix peuvent être révisés avec un préavis de 30 jours, avant le renouvellement mensuel pour les
          abonnements self-service et avant le renouvellement annuel pour les licences.</p>
        </Sub>
        <Sub title="4.2 Facturation">
          <List items={[
            'Les abonnements Essentiel, Pro et Cabinet sont facturés mensuellement, à la date anniversaire de la souscription',
            "Les analyses au-delà du forfait mensuel sont facturées sur la même échéance mensuelle que l'abonnement",
            'La licence annuelle est facturée à la commande',
            'Le setup one-shot est facturé avant démarrage des prestations',
            "Les dépassements d'une licence Entreprise sont constatés chaque mois civil et facturés à terme échu",
          ]} />
          <p>Le Client communique à Evalens, lors de la souscription ou à sa demande, son numéro SIREN, sa
          dénomination et son adresse de facturation, exacts et à jour, et l'informe sans délai de toute
          modification. Ces informations figurent sur les factures et permettent leur transmission au format
          électronique ; une information inexacte ou manquante peut en empêcher l'émission.</p>
        </Sub>
        <Sub title="4.3 Paiement">
          <p>Le paiement s'effectue par carte bancaire (CB) ou virement SEPA. Tout retard de paiement entraîne
          l'application de pénalités de retard au taux de trois fois le taux d'intérêt légal (article L. 441-10
          du Code de commerce), ainsi que d'une indemnité forfaitaire de recouvrement de 40 €. En cas de non-paiement après mise en demeure restée sans effet 8
          jours, Evalens se réserve le droit de suspendre l'accès au service.</p>
        </Sub>
      </Article>

      <Article n={5} title="Utilisation du service et obligations du Client">
        <Sub title="5.1 Utilisation conforme">
          <p>Le Client s'engage à utiliser Normascan exclusivement pour des finalités professionnelles légitimes et
          conformément aux présentes CGU/CGV. Il est notamment interdit de :</p>
          <List items={[
            "Tenter de contourner les mesures de sécurité ou de contrôle d'accès",
            'Soumettre des documents ne correspondant pas à des rapports techniques réels',
            'Revendre, sous-licencier ou partager l\'accès à des tiers non autorisés par le contrat',
            "Utiliser le service pour entraîner des modèles d'IA concurrents",
            'Tenter de décompiler, désassembler ou effectuer de l\'ingénierie inverse sur Normascan',
            'Contourner les limites contractuelles, notamment en multipliant les comptes ou les abonnements (article 3.5)',
          ]} />
        </Sub>
        <Sub title="5.2 Responsabilité des données uploadées">
          <p>Le Client est seul responsable des documents qu'il uploade sur Normascan. Il garantit qu'il dispose
          des droits nécessaires sur ces documents et qu'ils ne contiennent pas de données illicites. Les rapports
          peuvent contenir des données personnelles (noms de propriétaires, diagnostiqueurs) : le Client s'assure
          de la licéité du traitement conformément au RGPD.</p>
        </Sub>
        <Sub title="5.3 Aide à la décision">
          <Callout tone="amber">
            <p><strong>IMPORTANT</strong> : Normascan est un outil d'aide à la décision basé sur l'intelligence
            artificielle. Ses résultats sont fournis à titre indicatif et ne constituent pas un avis professionnel
            certifié. Le professionnel demeure seul responsable de l'interprétation des résultats et de toute
            décision prise sur cette base. Evalens ne peut être tenu responsable d'une erreur d'interprétation du
            rapport source par le modèle d'IA ni d'une évolution normative non encore intégrée.</p>
          </Callout>
          <p className="mt-2">Tout rapport d'analyse produit par Normascan est accompagné d'un avertissement légal
          intégral relatif à l'utilisation de l'intelligence artificielle (voir <Link href="/avertissement-ia" className="underline">Annexe « Avertissement IA »</Link>).
          L'utilisateur s'engage à ne pas supprimer, altérer ou tronquer cet avertissement lors de toute
          communication à un tiers.</p>
        </Sub>
      </Article>

      <Article n={6} title="Technologies tierces">
        <p>Normascan fait appel à des prestataires technologiques tiers pour la fourniture du service :</p>
        <DataTable
          headers={['Prestataire', 'Rôle', "Conditions d'utilisation"]}
          rows={[
            ['Mistral AI SAS (Paris, France)', "Analyse IA des rapports (LLM) et conversion OCR des PDFs scannés", "mistral.ai/terms — Evalens respecte les conditions d'utilisation Mistral AI. Contrat API professionnel Mistral AI (plan Scale) — pas d'entraînement des modèles. Les données transmises à Mistral AI ne sont pas utilisées pour l'entraînement de leurs modèles dans le cadre du contrat API professionnel."],
            ['OVH SAS (Roubaix, France)', 'Hébergement infrastructure (serveurs VPS, stockage)', 'ovh.com/fr/support/termes-conditions — Hébergement exclusivement en France (UE).'],
            ['Stripe Payments Europe, Ltd. (Dublin, Irlande)', 'Encaissement des abonnements Essentiel, Pro et Cabinet (carte bancaire, prélèvement SEPA)', 'stripe.com/fr/legal — Les données complètes de carte bancaire sont saisies directement auprès de Stripe et ne transitent pas par Normascan. Les éventuels transferts internationaux de données par Stripe sont décrits dans la Politique de Confidentialité. '],
          ]}
        />
        <p>En utilisant Normascan, le Client est informé qu'Evalens recourt aux prestataires tiers mentionnés ci-dessus,
          chacun pour le rôle indiqué et dans les conditions décrites dans la <Link href="/privacy" className="underline">Politique de confidentialité</Link>.
          Les rapports uploadés ne sont transmis qu'à Mistral AI et OVH. Pour les abonnements self-service,
          les données complètes de carte bancaire sont saisies directement auprès de Stripe et ne transitent pas par Normascan ;
          les transferts internationaux éventuellement réalisés par Stripe sont décrits dans la Politique de Confidentialité.</p>
      </Article>

      <Article n={7} title="Propriété intellectuelle">
        <Sub title="7.1 Droits Evalens">
          <p>Normascan, son code source, ses algorithmes, ses interfaces, ses bases de données, ses modèles IA
          fine-tunés et toute innovation développée par Evalens sont et demeurent la propriété exclusive
          d'Evalens, protégés par le droit d'auteur (Code de la propriété intellectuelle), les droits sur les
          bases de données, et le secret des affaires.</p>
          <p>La marque Normascan est déposée à l'INPI (n° 5242986, classes 9 et 42). Toute utilisation non
          autorisée est constitutive de contrefaçon.</p>
        </Sub>
        <Sub title="7.2 Licence accordée au Client">
          <p>Evalens accorde au Client une licence d'utilisation non exclusive, non cessible et non
          sous-licenciable de Normascan, pour la durée du contrat et dans les limites stipulées dans le contrat
          souscrit.</p>
        </Sub>
        <Sub title="7.3 Données du Client">
          <p>Le Client conserve la pleine propriété de ses rapports uploadés et de ses données. Evalens n'acquiert
          aucun droit sur ces données. Les résultats d'analyse produits par Normascan sur les données du Client
          appartiennent au Client.</p>
        </Sub>
      </Article>

      <Article n={8} title="Responsabilité et garanties">
        <Sub title="8.1 Limitation de responsabilité Evalens">
          <p>Evalens s'engage à fournir ses services avec diligence et dans le respect des règles de l'art.
          Toutefois, sa responsabilité est limitée comme suit :</p>
          <List items={[
            'Normascan est fourni comme outil d\'aide à la décision — les résultats ne constituent pas des certifications ou avis professionnels',
            "La responsabilité d'Evalens est plafonnée aux sommes effectivement encaissées au titre de la licence sur les 12 derniers mois",
            "Evalens ne saurait être responsable des dommages indirects, pertes de revenus, préjudices commerciaux résultant d'une erreur d'analyse",
            "Evalens ne garantit pas que Normascan est exempt de tout défaut mais s'engage à corriger tout bug identifié dans des délais raisonnables",
          ]} />
        </Sub>
        <Sub title="8.2 Assurance">
          <p>Evalens est assurée en Responsabilité Civile Professionnelle pour son activité d'éditeur du
          logiciel Normascan. Attestation disponible sur demande.</p>
        </Sub>
      </Article>

      <Article n={9} title="Confidentialité et RGPD">
        <p>Evalens s'engage à traiter les données personnelles conformément au RGPD (Règlement UE 2016/679) et à
        la loi Informatique et Libertés. La collecte et le traitement des données sont détaillés dans la <Link href="/privacy" className="underline">Politique
        de Confidentialité</Link>.</p>
        <p><strong>Base légale du traitement</strong> : exécution du contrat (licence Normascan) et intérêt
        légitime d'Evalens (amélioration du service).</p>
        <p><strong>Durée de conservation</strong> : les rapports PDF déposés sont supprimés automatiquement six
        mois après leur dépôt. Les résultats d'analyse sont conservés tant que le Compte existe ; l'Utilisateur
        peut les supprimer à tout moment. La suppression du Compte efface les rapports déposés et les résultats
        d'analyse ; les sauvegardes qui pourraient encore les contenir sont écrasées dans un délai de 30 jours.
        Pour une licence Entreprise, les stipulations de son contrat s'appliquent. Les factures sont conservées
        dix ans, conformément à la loi.</p>
        <p><strong>Droits des personnes concernées</strong> : droit d'accès, rectification, suppression,
        portabilité. À exercer à : privacy@evalens.fr.</p>
      </Article>

      <Article n={10} title="Force majeure">
        <p>Ni l'une ni l'autre des parties ne saurait être tenue pour responsable de l'inexécution de ses
        obligations si cette inexécution résulte d'un cas de force majeure au sens de l'article 1218 du Code
        civil, notamment : catastrophes naturelles, pandémies, actes de guerre, pannes majeures d'infrastructures
        internet hors contrôle d'Evalens, défaillances prolongées des services cloud tiers.</p>
      </Article>

      {/* ⚠️ Réécrit le 24/09/2026 DANS la version 1.3, sans nouvelle version, sur
          décision de Marc : l'ancien texte prévoyait une acceptation TACITE
          (« l'utilisation continue du service vaut acceptation ») alors que
          l'application demande désormais une acceptation EXPRESSE à la connexion
          (chantier SaaS 1ter). Il décrit ce que fait le code : notification
          (`core/notification_cgv.py`), versions datées et archivées (`core/cgv.py`,
          `/cgv`), acceptation et blocage des analyses (`cgv_a_accepter`),
          version conservée par compte et au paiement. */}
      <Article n={11} title="Modifications des CGU/CGV">
        <p>Evalens peut modifier les présentes CGU/CGV. Chaque version est datée : elle est publiée sur le
        site avec sa date d'entrée en vigueur, et les versions antérieures restent consultables.</p>
        <p>Toute modification substantielle est notifiée au Client par email, avec le texte de la nouvelle
        version, au moins 30 jours avant son entrée en vigueur. Les modifications non substantielles
        (précisions, corrections, mise en conformité avec la réglementation) peuvent entrer en vigueur dès
        leur publication. Aucune version ne s'applique rétroactivement.</p>
        <p>À compter de son entrée en vigueur, la nouvelle version est présentée à l'Utilisateur lors de sa
        connexion au Service et doit être acceptée expressément. Tant qu'elle ne l'est pas, aucune nouvelle
        analyse ne peut être lancée ; l'accès aux Résultats d'analyse déjà produits est conservé.</p>
        <p>Le Client qui n'accepte pas la nouvelle version peut résilier son abonnement Essentiel, Pro ou
        Cabinet à tout moment, dans les conditions de l'article 3.4. Pour une licence Entreprise ou Grand
        Compte, les stipulations de son contrat s'appliquent.</p>
        <p>La version acceptée et la date de son acceptation sont conservées pour chaque Compte, de même que
        la version en vigueur au jour de chaque souscription.</p>
      </Article>

      <Article n={12} title="Droit applicable et litiges">
        <p>Les présentes CGU/CGV sont soumises au droit français. En cas de litige, les parties s'engagent à
        tenter de régler le différend à l'amiable dans un délai de 30 jours. À défaut d'accord, le Tribunal de
        Commerce compétent du ressort du siège social d'Evalens sera seul compétent, même en cas de pluralité de
        défendeurs ou d'appel en garantie.</p>
      </Article>
    </>
  );
}

// ── Versions des CGU/CGV (chantier SaaS 1ter, 24/09/2026) ─────────────────
//
// ⚠️ MIROIR du calendrier de `core/cgv.py`, qui fait foi (c'est lui qu'on
// enregistre à l'acceptation). `tests/unit/test_cgv.py` fait échouer la CI si
// un numéro ou une date diverge, ou si la version en vigueur ou à venir du
// calendrier n'a pas de texte ici. La liste sert aussi de repli quand la page
// s'affiche avant la réponse de `/api/cgv/versions`.
//
// PRÉPARER UNE NOUVELLE VERSION (ex. 1.4), dans cet ordre :
//   1. copier `CGVContent` en `CGVContent_1_3` (le texte en vigueur est FIGÉ) ;
//   2. rédiger la 1.4 dans `CGVContent` lui-même — ⛔ ne pas le renommer :
//      `Juridiques et Contractuels/_generation/extraire_legal.js` le cherche
//      par ce nom pour produire le docx et le PDF de la nouvelle version ;
//   3. ici : `{ numero: '1.3', …, Texte: CGVContent_1_3 }` puis
//      `{ numero: '1.4', en_vigueur_le: 'AAAA-MM-JJ', Texte: CGVContent }` ;
//   4. la même ligne 1.4, avec son résumé, dans `CALENDRIER` de `core/cgv.py` ;
//   5. après l'entrée en vigueur, retirer `CGVContent_1_3` : son PDF figé
//      (archive publique) prend le relais.
//
// Seules les versions dont le TEXTE est encore dans le bundle figurent ici ;
// les plus anciennes se lisent dans leur PDF archivé.
// 29/09/2026 : texte figé de la 1.3 retiré à l'entrée en vigueur de la 1.4 —
// elle se lit désormais dans `/legal/cgv-1.3.pdf` (archive de `core/cgv.py`).
const CGV_VERSIONS = [
  { numero: '1.4', en_vigueur_le: '2026-09-29', Texte: CGVContent },
];

// Date du jour du NAVIGATEUR, au format des dates du calendrier. Ne sert que de
// repli le temps que la réponse du serveur arrive : c'est elle qui décide.
const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// `AAAA-MM-JJ` ➜ `JJ/MM/AAAA`, comme les autres en-têtes de ces documents.
const dateFr = (iso) => iso.split('-').reverse().join('/');

// { en_vigueur, a_venir, versions } : les NUMÉROS affichables, et l'historique
// du serveur (numéro, date, PDF) pour l'archive. Serveur d'abord ; une réponse
// qui citerait une version sans texte dans le bundle est ignorée. `versions`
// reste vide tant que le serveur n'a pas répondu : l'archive attend.
function useCalendrierCgv() {
  const connue = (numero) => CGV_VERSIONS.some(v => v.numero === numero);
  const local = () => {
    const jour = aujourdhuiIso();
    const passees = CGV_VERSIONS.filter(v => v.en_vigueur_le <= jour);
    return {
      en_vigueur: (passees[passees.length - 1] || CGV_VERSIONS[0]).numero,
      a_venir: (CGV_VERSIONS.find(v => v.en_vigueur_le > jour) || {}).numero || null,
      versions: [],
    };
  };
  const [calendrier, setCalendrier] = React.useState(local);
  React.useEffect(() => {
    getCgvVersions()
      .then(d => {
        const enVigueur = d.en_vigueur && d.en_vigueur.numero;
        const aVenir = d.a_venir && d.a_venir.numero;
        if (!connue(enVigueur)) return;
        setCalendrier({ en_vigueur: enVigueur, a_venir: connue(aVenir) ? aVenir : null,
                        versions: d.versions || [] });
      })
      .catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  return calendrier;
}

// Une version DEMANDÉE (`?version=`) dont le texte n'est pas dans le bundle.
// ⚠️ Jamais de repli silencieux : afficher la version en vigueur à qui a suivi
// un lien vers une autre ferait lire un contrat pour un autre (constaté par
// Marc au test du 24/09/2026). On dit ce qui se passe, et où trouver le PDF.
function BandeauVersionIndisponible({ numero, calendrier }) {
  const { t } = useI18n();
  const archivee = calendrier.versions.find(v => v.numero === numero);
  let contenu;
  if (!calendrier.versions.length) {
    return null;  // le serveur n'a pas encore répondu : ne rien affirmer
  } else if (archivee && archivee.pdf) {
    contenu = (<>
      {t('cgv_page_hors_ligne').replace('{v}', numero)}{' '}
      <a href={archivee.pdf} className="font-medium underline" target="_blank" rel="noopener noreferrer">
        {t('cgv_page_telecharger_pdf').replace('{v}', numero)}
      </a>
    </>);
  } else if (archivee) {
    contenu = t('cgv_page_sans_pdf').replace('{v}', numero);
  } else {
    contenu = t('cgv_page_inconnue').replace('{v}', numero);
  }
  return (
    <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      {contenu}
    </div>
  );
}

// Archive publique : chaque version qui a un PDF figé (règle 5 du chantier).
function ArchiveCgv({ calendrier }) {
  const { t } = useI18n();
  const avecPdf = calendrier.versions.filter(v => v.pdf).slice().reverse();
  if (!avecPdf.length) return null;
  return (
    <div className="mt-6 text-sm text-gray-600">
      <div className="font-medium text-gray-800 mb-1">{t('cgv_page_archive_titre')}</div>
      <ul className="list-disc pl-5">
        {avecPdf.map(v => (
          <li key={v.numero}>
            <a href={v.pdf} className="hover:underline" target="_blank" rel="noopener noreferrer">
              {t('cgv_page_archive_ligne').replace('{v}', v.numero).replace('{date}', dateFr(v.en_vigueur_le))}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Bandeau de la page CGV pendant un préavis, ou quand on lit une autre version
// que celle en vigueur (`/cgv?version=1.4`, lien de l'e-mail de notification).
// Liens `<a>` et non `<Link>` : seule la requête change, et le routeur ne
// réaffiche pas la page sur un simple changement de `?version=`.
function BandeauVersionCgv({ affichee, calendrier }) {
  const { t } = useI18n();
  const version = (n) => CGV_VERSIONS.find(v => v.numero === n);
  const remplir = (cle, v) => t(cle).replace('{v}', v.numero).replace('{date}', dateFr(v.en_vigueur_le));
  const enVigueur = version(calendrier.en_vigueur);
  const aVenir = calendrier.a_venir && version(calendrier.a_venir);

  let texte = null;
  let lien = null;
  if (affichee.numero === calendrier.en_vigueur) {
    if (!aVenir) return null;
    texte = remplir('cgv_page_a_venir', aVenir);
    lien = { href: `/cgv?version=${aVenir.numero}`, libelle: remplir('cgv_page_lire', aVenir) };
  } else {
    texte = aVenir && affichee.numero === aVenir.numero
      ? remplir('cgv_page_lecture_a_venir', aVenir)
      : remplir('cgv_page_lecture_ancienne', affichee);
    lien = { href: '/cgv', libelle: remplir('cgv_page_lire_en_vigueur', enVigueur) };
  }
  return (
    <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      {texte}{' '}
      <a href={lien.href} className="font-medium underline">{lien.libelle}</a>
    </div>
  );
}

// ── Avertissement IA (Annexe A) ──────────────────────────────────────────

function AvertissementIAContent() {
  return (
    <>
      <Callout tone="blue">
        <p>La présente annexe fait partie intégrante des Conditions Générales d'Utilisation et de Vente de
        Normascan (Evalens SASU), consultables à l'adresse <Link href="/cgv" className="underline">app.normascan.fr/cgv</Link>. En
        cas de contradiction entre la présente annexe et les CGU, les dispositions de l'annexe prévalent pour tout
        ce qui concerne la nature, l'utilisation et les limites des rapports d'analyse générés par le service.</p>
      </Callout>

      <Article title="1. Nature et portée des documents générés">
        <p>Les rapports d'analyse générés via la plateforme SaaS Normascan constituent une <strong>aide à
        l'analyse technique</strong> fondée sur la norme <strong>NF X 46-020</strong> relative au repérage des
        matériaux et produits contenant de l'amiante, ainsi que sur le fascicule d'application <strong>FD X
        46-041</strong> (dans sa version applicable à la date de génération). Il est produit par un système
        d'intelligence artificielle (modèle <strong>Norma</strong>) et présenté à titre indicatif uniquement.</p>
        <p>Les résultats, commentaires, suggestions, remarques et avis formulés dans ces rapports <strong>ne se
        substituent en aucun cas à l'expertise d'un professionnel qualifié et certifié</strong>, notamment d'un
        diagnostiqueur amiante certifié conformément à l'article R. 4412-97-4 du Code du travail et aux exigences
        de la norme NF X 46-020.</p>
      </Article>

      <Article title="2. Obligation de vérification et de validation humaine">
        <p>Conformément à l'<strong>article 50 du Règlement (UE) 2024/1689</strong> (AI Act) relatif aux
        obligations de transparence des systèmes d'intelligence artificielle, l'utilisateur est expressément
        informé qu'il interagit avec un système d'IA générant du contenu artificiel.</p>
        <p>Par conséquent :</p>
        <List items={[
          "Toutes les informations, avis, commentaires, suggestions et/ou remarques générés par Norma doivent être impérativement vérifiés, relus et validés par un professionnel certifié avant toute prise de décision, engagement contractuel ou mesure de sécurité",
          "Les rapports d'analyse générés sont une aide à la décision qui doit être interprétée par une personne qualifiée disposant des compétences techniques et réglementaires requises dans le domaine du repérage amiante et de la prévention des risques liés aux travaux sur matériaux amiantés",
          "Les conclusions des rapports d'analyse générés ne peuvent se substituer à l'expertise d'un professionnel qualifié et ne constituent en aucune manière un avis technique, une attestation de conformité ou un diagnostic au sens réglementaire",
        ]} />
      </Article>

      <Article title="3. Base documentaire et sources de l'analyse">
        <p>L'analyse produite repose <strong>exclusivement</strong> sur les données et documents transmis par
        l'utilisateur dans le cadre de sa session, à savoir :</p>
        <List items={[
          'Le rapport de repérage amiante soumis à l\'analyse',
          'Les documents annexes éventuellement téléversés',
          'Les saisies libres et éléments de contexte renseignés par l\'utilisateur dans le cadre du « mode projet » de Normascan',
        ]} />
        <p>Les résultats reflètent uniquement le traitement algorithmique de ces données d'entrée et la
        comparaison avec les connaissances du modèle concernant la norme NF X 46-020 et le fascicule FD X 46-041.
        Ils ne présument pas de l'exactitude, de l'exhaustivité ou de la fiabilité des documents initiaux fournis
        par l'utilisateur.</p>
      </Article>

      <Article title="4. Limites et risques d'erreur du système d'intelligence artificielle">
        <p>L'utilisateur est informé que le modèle Norma, bien que paramétré et affiné par EVALENS SASU pour le
        domaine du repérage amiante, demeure un système d'intelligence artificielle susceptible de :</p>
        <List items={[
          'Produire des erreurs, inexactitudes, omissions ou interprétations erronées',
          'Interpréter de manière incorrecte des données ambiguës, partielles ou contradictoires',
          "Ignorer des éléments contextuels non transmis dans les documents ou saisies d'entrée",
          'Générer des réponses fondées sur des connaissances statistiques qui ne reflètent pas la situation spécifique du site ou du bâtiment concerné',
        ]} />
        <p>EVALENS SASU ne saurait être tenue responsable des conséquences directes ou indirectes résultant d'une
        utilisation du présent rapport sans validation préalable par un professionnel compétent.</p>
      </Article>

      <Article title="5. Protection des données à caractère personnel (RGPD)">
        <p>Les informations contenues dans les rapports analysés et transmises par l'utilisateur ont été
        communiquées en toute connaissance de cause et sous la responsabilité exclusive de l'utilisateur.</p>
        <Sub title="Responsabilité de l'utilisateur">
          <p>L'utilisateur garantit détenir l'ensemble des droits et autorisations nécessaires à la transmission
          des documents et données soumis à l'analyse, y compris lorsque ceux-ci comportent des données à
          caractère personnel (nom et coordonnées du diagnostiqueur, adresse du site, photographies, etc.).
          L'utilisateur s'assure notamment que la transmission est conforme aux finalités poursuivies et aux
          bases légales applicables (exécution d'une mission d'intérêt public, intérêt légitime, ou consentement
          lorsque requis).</p>
        </Sub>
        <Sub title="Responsabilité d'EVALENS SASU">
          <p>EVALENS SASU agit au titre du règlement (UE) 2016/679 (RGPD) soit en qualité de responsable de
          traitement lorsque l'utilisateur agit pour son propre compte, soit en qualité de sous-traitant
          (processeur) pour le compte de l'organisation cliente, dans les conditions de son contrat et, pour une
          licence Entreprise, de son accord de traitement des données (DPA). Les données sont hébergées sur des serveurs situés en France (OVH
          SAS) et ne font l'objet d'aucun transfert hors de l'Espace économique européen.</p>
        </Sub>
        <Sub title="Nature des données">
          <p>Les rapports de repérage amiante analysés par Normascan relèvent principalement de données
          techniques et professionnelles. Selon leur contenu réel, ils peuvent comporter des données à caractère
          personnel (identité du diagnostiqueur, coordonnées de l'opérateur, etc.). Sauf cas particulier, ils ne
          constituent pas des données sensibles au sens de l'article 9 du RGPD (données de santé, données
          biométriques) dans la mesure où ils portent sur la caractérisation technique de matériaux d'un bâtiment
          et non sur l'état de santé d'une personne physique. Lorsqu'un rapport contiendrait des données
          sensibles, la transmission relève de la seule responsabilité et du contrôle de l'utilisateur.</p>
        </Sub>
        <Sub title="Destinataires et publication">
          <p>L'utilisateur est informé que les rapports de repérage amiante sont parfois rendus publics dans le
          cadre d'appels d'offres publics ou de consultations de marchés. Dans ce cas, les données qu'ils
          contiennent sont déjà accessibles au public. Lorsqu'ils sont de nature privée, leur transmission à
          Normascan implique une mise à disposition aux fins d'analyse exclusive, sans réutilisation commerciale,
          pour la durée de conservation définie à l'article 9 des <Link href="/cgv" className="underline">CGU/CGV</Link>.</p>
        </Sub>
      </Article>

      <Article title="6. Information sur le traitement par IA et obligations du AI Act">
        <p>Conformément à l'article 50 du Règlement (UE) 2024/1689 (AI Act), le présent document est généré par un
        système d'intelligence artificielle. EVALENS SASU, en sa qualité de fournisseur et de déployeur du
        système Norma, s'engage à :</p>
        <List items={[
          'Informer clairement l\'utilisateur de la nature artificielle des contenus générés',
          'Décrire les caractéristiques, capacités et limitations du système (cf. présent avertissement)',
          'Garantir que l\'utilisateur conserve à tout instant la maîtrise de la décision finale et la responsabilité de l\'interprétation des résultats',
        ]} />
        <p>Le système Norma n'est pas classé comme système d'IA à haut risque au sens du chapitre III du AI Act.
        Il relève des systèmes d'IA soumis à des obligations de transparence au titre du chapitre IV dudit
        règlement.</p>
      </Article>

      <Article title="7. Propriété intellectuelle et réutilisation">
        <p>Les rapports sont générés pour le compte exclusif de l'utilisateur. Leur contenu peut être réutilisé
        par l'utilisateur sous réserve de la non altération, suppression ou troncation de toutes ou parties des
        informations présentes, des mentions, avertissement et de la mention de sa source (Normascan / EVALENS
        SASU). Toute suppression, altération ou troncation est interdite.</p>
      </Article>

      <Article title="8. Limitation de responsabilité">
        <p>En toute hypothèse, la responsabilité d'EVALENS SASU au titre d'un rapport généré est expressément
        limitée au montant versé par l'utilisateur au titre de l'analyse concernée, dans les conditions et
        limites définies aux <Link href="/cgv" className="underline">Conditions Générales d'Utilisation</Link> et
        aux <Link href="/sla" className="underline">Conditions de Service applicables (SLA)</Link>.</p>
      </Article>
    </>
  );
}

// ── Politique de confidentialité (RGPD) ─────────────────────────────────────

function PrivacyContent() {
  return (
    <>
      <Article title="1. Responsable du traitement">
        <p><strong>Responsable :</strong> EVALENS, SASU au capital de 5 000 €<br />
        <strong>Siège social :</strong> 58 rue de Monceau CS 48756 75380 Paris Cedex 08<br />
        <strong>RCS :</strong> 103593489 — Greffe de PARIS<br />
        <strong>Représentant légal :</strong> M. Marc FESSARD, Président<br />
        <strong>Contact protection des données :</strong> privacy@evalens.fr</p>
      </Article>

      <Article title="2. Données collectées et finalités">
        <Sub title="2.1 Données de compte">
          <p>Lors de la création d'un compte Normascan :</p>
          <List items={[
            'Nom, prénom, adresse email professionnelle',
            'Dénomination, SIREN et adresse de facturation de l\'organisation',
            'Numéro de téléphone (optionnel)',
          ]} />
          <p>Lors de la souscription d'un abonnement, les données de paiement (carte bancaire ou coordonnées
          bancaires SEPA) sont saisies directement chez Stripe : Normascan ne les reçoit ni ne les conserve.</p>
          <p><strong>Finalité :</strong> gestion du compte, facturation, support. <strong>Base légale :</strong> exécution
          du contrat (Art. 6.1.b RGPD).</p>
          <p><strong>Finalité :</strong> vérification du respect des conditions de souscription (CGU/CGV, art. 3.5),
          par le rapprochement du numéro SIREN et du domaine de l'adresse de messagerie des comptes abonnés.
          <strong> Base légale :</strong> intérêt légitime d'Evalens (Art. 6.1.f RGPD). Toute personne peut s'y
          opposer à l'adresse privacy@evalens.fr.</p>
        </Sub>
        <Sub title="2.2 Données de navigation et d'usage">
          <p>Lors de l'utilisation de la plateforme :</p>
          <List items={[
            'Logs de connexion (adresse IP, navigateur, heure)',
            'Actions sur la plateforme (analyses lancées, exports téléchargés)',
            'Données techniques de performance et de débogage',
          ]} />
          <p><strong>Finalité :</strong> sécurité, amélioration du service, gestion des incidents. <strong>Base
          légale :</strong> intérêt légitime d'Evalens (Art. 6.1.f RGPD).</p>
        </Sub>
        <Sub title="2.3 Rapports et données techniques uploadés">
          <p>Les rapports de diagnostic (amiante, plomb, etc.) uploadés par l'Utilisateur peuvent contenir :</p>
          <List items={[
            'Informations sur les biens immobiliers diagnostiqués (adresse, superficie, année de construction)',
            'Identité et coordonnées du propriétaire / bailleur',
            'Identité et certification du diagnostiqueur signataire',
            'Données techniques de mesure (résultats d\'analyses amiante, plomb, etc.)',
          ]} />
          <p><strong>Finalité :</strong> analyse automatisée du rapport par l'IA et production d’une extraction au
          format xls et d’un rapport d’analyse en format pdf. <strong>Base légale :</strong> exécution du contrat (Art. 6.1.b RGPD) — le traitement est la
          prestation principale commandée.</p>
          <p>Les résultats d'analyse sont produits par un système d'intelligence artificielle et sont accompagnés
          d'un avertissement légal relatif aux limites de l'IA (voir <Link href="/avertissement-ia" className="underline">Annexe
          « Avertissement IA »</Link> du rapport généré et des <Link href="/cgv" className="underline">Conditions
          Générales d'Utilisation</Link>). Les rapports contiennent potentiellement des données à caractère
          personnel ; l'utilisateur est seul responsable de la licéité de leur transmission.</p>
          <p><strong>Durée de conservation :</strong> Les rapports PDF déposés sont supprimés automatiquement six mois après leur dépôt. Les résultats d'analyse sont conservés tant que le compte existe, et l'utilisateur peut les supprimer à tout moment. La suppression du compte efface les rapports déposés et les résultats d'analyse. Les sauvegardes qui pourraient encore les contenir sont écrasées dans un délai de 30 jours.
          Licence Entreprise : les stipulations de son contrat s'appliquent. Les données de compte sont conservées pendant la durée d’utilisation du compte et de la relation contractuelle. Après sa clôture, seules les données strictement nécessaires à la preuve de la relation contractuelle et à l’exercice ou à la défense des droits d’EVALENS sont archivées, avec un accès restreint, pendant une durée maximale de cinq ans à compter de la fin de la relation contractuelle, puis supprimées ou anonymisées, sauf litige en cours ou obligation légale imposant leur conservation. 
          Les factures et pièces justificatives comptables sont conservées pendant dix ans conformément à l’article L. 123-22 du Code de commerce. </p>
        </Sub>
      </Article>

      <Article title="3. Prestataires, destinataires, transferts de données">
        <DataTable
          headers={['Sous-traitant', 'Rôle', 'Pays / Zone', 'Garanties RGPD']}
          rows={[
            ['Mistral AI SAS', 'Analyse IA des rapports (LLM Large) et OCR des PDFs scannés', 'France (UE) — Serveurs EU', "Société française (Paris). Data Processing Agreement de Mistral AI (legal.mistral.ai/terms/data-processing-addendum), intégré aux conditions d'utilisation du service API. Les données transmises via l'API ne sont PAS utilisées pour entraîner les modèles Mistral (contrat API professionnel plan Scale). Politique : mistral.ai/privacy"],
            ['OVH SAS', 'Hébergement serveurs — stockage données et rapports', 'France (Roubaix, UE)', 'Hébergeur français. Données exclusivement en France. Politique : ovh.com/fr/personal-data-protection'],
            ['Qonto SAS', 'Gestion facturation et paiements', 'France (UE)', 'Établissement de paiement agréé ACPR. RGPD conforme. Données financières uniquement.'],
            ['Stripe Payments Europe, Ltd.', 'Encaissement des abonnements (carte bancaire, prélèvement SEPA)', 'Irlande (UE) ; transferts possibles vers des pays tiers (voir ci-dessous)', "Les données complètes de carte sont saisies directement auprès de Stripe et ne transitent pas par Normascan. Le rôle RGPD de Stripe varie selon les traitements. Voir les informations ci-dessous et la documentation contractuelle et de confidentialité de Stripe. "],
          ]}
        />
        <p><strong>Localisation des rapports et résultats :</strong> Les rapports techniques sont transmis à Mistral AI SAS, société française, pour analyse. 
        Mistral indique utiliser des serveurs situés dans l'Union européenne. Les rapports et résultats sont hébergés sur l'infrastructure d'OVH SAS située en France. 
        Ils ne sont pas transmis à Stripe. Ces indications concernent les rapports et résultats d'analyse, et non les données de paiement.</p>
        <p><strong>Transferts liés aux paiements :</strong> Les données nécessaires au paiement des abonnements self-service sont recueillies directement par Stripe Payments Europe, 
        Ltd. ; les données complètes de carte bancaire ne transitent pas par Normascan. 
        Stripe indique que certaines données personnelles traitées dans le cadre de ses services peuvent être transférées vers les États-Unis, 
        l'Inde et d'autres pays où Stripe ou ses prestataires exercent leurs activités. 
        Pour les transferts soumis au RGPD, Stripe indique s'appuyer, selon le pays de destination et le transfert concerné, 
        sur une décision d'adéquation ou sur des garanties appropriées, notamment les clauses contractuelles types de la Commission européenne. 
        Pour les transferts vers les États-Unis, Stripe indique recourir au cadre EU-U.S. Data Privacy Framework lorsque le destinataire est certifié 
        et que le transfert entre dans le champ de cette certification. Les informations détaillées sur les transferts et les garanties sont disponibles 
        dans le centre de confidentialité Stripe et le Data Transfers Addendum de Stripe. 
        Une copie des clauses contractuelles types peut être demandée à Stripe via son centre de confidentialité.</p>
        <p><strong>DPA avec Mistral AI :</strong> Evalens s'appuie sur les garanties contractuelles de Mistral AI, 
        décrites dans le Data Processing Agreement (DPA) intégré aux conditions d'utilisation du service API, conformément à l'article 28 du RGPD. 
        Les rapports transmis à Mistral AI sont traités exclusivement pour l'exécution de la prestation d'analyse, sans utilisation secondaire.</p>
      </Article>

      <Article title="4. Droits des personnes concernées">
        <p>Conformément au RGPD, toute personne dont les données sont traitées dispose des droits suivants :</p>
        <List items={[
          "Droit d'accès (Art. 15) : obtenir confirmation du traitement et copie des données",
          'Droit de rectification (Art. 16) : corriger des données inexactes ou incomplètes',
          "Droit à l'effacement (Art. 17) : obtenir la suppression des données dans certaines conditions",
          'Droit à la limitation (Art. 18) : suspendre le traitement temporairement',
          'Droit à la portabilité (Art. 20) : recevoir ses données dans un format structuré',
          "Droit d'opposition (Art. 21) : s'opposer à un traitement basé sur l'intérêt légitime",
        ]} />
        <p>Pour exercer ces droits : privacy@evalens.fr — Délai de réponse : 30 jours maximum.</p>
        <p>En cas de réclamation non satisfaite, vous pouvez saisir la CNIL (Commission Nationale de
        l'Informatique et des Libertés) sur cnil.fr.</p>
      </Article>

      <Article title="5. Sécurité des données">
        <p>Evalens met en œuvre les mesures techniques et organisationnelles appropriées pour assurer la sécurité
        des données :</p>
        <List items={[
          'Chiffrement des communications HTTPS/TLS obligatoire',
          'Authentification JWT avec expiration et rotation des tokens',
          'Accès aux données restreint aux utilisateurs autorisés du compte client',
          'Sauvegardes quotidiennes chiffrées conservées 30 jours',
          'Rate limiting et protection contre les attaques DDoS',
          "Logs d'accès et d'audit conservés 12 mois",
          'Politique de mots de passe robuste et authentification sécurisée',
        ]} />
      </Article>

      <Article title="6. Cookies et traceurs">
        <p>Normascan utilise un nombre limité de cookies strictement nécessaires au fonctionnement du service
        (session utilisateur, préférences de langue). Aucun cookie publicitaire ou de traçage tiers n'est
        utilisé. Le service est exempt de publicité.</p>
      </Article>

      <Article title="7. Modifications de la politique">
        <p>Cette politique peut être mise à jour. Toute modification substantielle sera notifiée aux Utilisateurs
        par email 30 jours avant son entrée en vigueur. La date de dernière mise à jour est indiquée en haut du
        document.</p>
      </Article>
    </>
  );
}

// ── Mentions légales ─────────────────────────────────────────────────────

function MentionsContent() {
  return (
    <>
      <p className="text-xs text-gray-500 mb-4">Conformément à la loi n° 2004-575 du 21 juin 2004 pour la
      Confiance dans l'Économie Numérique (LCEN), Art. 6-I-1.</p>

      <Article title="1. Éditeur du service">
        <p>Le site https://app.normascan.fr et le service Normascan (marque déposée à l'INPI sous le n°
        5242986, classes 9 et 42) sont édités par :</p>
        <DataTable
          headers={['', '']}
          rows={[
            ['Dénomination sociale', 'EVALENS'],
            ['Forme juridique', 'Société par Actions Simplifiée Unipersonnelle (SASU)'],
            ['Capital social', '5 000 euros'],
            ['Siège social', '58 rue de Monceau CS 48756 75380 Paris Cedex 08'],
            ['Numéro RCS', '103593489 — Greffe du Tribunal de Commerce de PARIS'],
            ['N° TVA intracommunautaire', 'FR45103593489'],
            ['Code APE / NAF', '6201Z — Programmation informatique'],
            ['Email de contact', 'contact@evalens.fr'],
            ['Site web', 'https://app.normascan.fr'],
          ]}
        />
      </Article>

      <Article title="2. Directeur de la publication">
        <DataTable
          headers={['', '']}
          rows={[
            ['Nom', 'Marc FESSARD'],
            ['Qualité', 'Président — Associé unique de la SASU EVALENS'],
            ['Contact', 'contact@evalens.fr'],
          ]}
        />
        <p className="text-xs text-gray-500">Conformément à l'article 6-I-2° de la LCEN, le directeur de
        publication est le représentant légal de la personne morale éditrice du service.</p>
      </Article>

      <Article title="3. Hébergement du service">
        <DataTable
          headers={['', '']}
          rows={[
            ['Hébergeur', 'OVH SAS'],
            ['Forme juridique', 'Société par Actions Simplifiée (SAS)'],
            ['Siège social', '2 rue Kellermann — 59100 ROUBAIX — FRANCE'],
            ['N° RCS', '424 761 419 RCS Lille Métropole'],
            ["Téléphone", "1007 (numéro d'assistance OVH)"],
            ['Site web', 'https://www.ovhcloud.com'],
            ['Localisation des données', 'Centres de données français (Union Européenne)'],
          ]}
        />
      </Article>

      <Article title="4. Prestataires et transferts de données">
        <DataTable
          headers={['Prestataire', 'Rôle', 'Pays', 'Conditions / Contact']}
          rows={[
            ['Mistral AI SAS', "Analyse IA des rapports (LLM) et reconnaissance optique de caractères (OCR)", 'France (Paris) — Serveurs UE', "mistral.ai/terms — Contrat API professionnel Mistral AI (plan Scale) — pas d'utilisation des données pour l'entraînement des modèles. DPA RGPD intégré aux conditions commerciales."],
            ['OVH SAS', 'Hébergement infrastructure (VPS, stockage, bases de données)', 'France (Roubaix) — UE', 'ovhcloud.com/fr/personal-data-protection — CGS RGPD Art. 28. Données exclusivement en France.'],
            ['Qonto SAS', 'Gestion des paiements et de la facturation', 'France (Paris) — UE', 'qonto.com — Établissement de paiement agréé ACPR. RGPD conforme.'],
            ['Stripe Payments Europe, Ltd.', 'Encaissement des abonnements (carte bancaire, prélèvement SEPA)', 'Irlande (Dublin) — UE ; transferts éventuels vers des pays tiers ', "Les données complètes de carte bancaire sont saisies directement auprès de Stripe et ne transitent pas par Normascan. Les rôles de Stripe et les informations sur les transferts internationaux éventuels sont précisés dans sa documentation et dans la Politique de Confidentialité d'Evalens : https://app.normascan.fr/privacy "],
          ]}
        />
      </Article>

      <Article title="5. Propriété intellectuelle">
        <Sub title="5.1 Marque Normascan">
          <DataTable
            headers={['', '']}
            rows={[
              ['Numéro de dépôt', '5242986'],
              ['Date de dépôt', '30 mars 2026'],
              ['Classes couvertes', 'Classe 9 (logiciels informatiques) et Classe 42 (services SaaS, analyse de données, intelligence artificielle)'],
              ['Titulaire', 'EVALENS, SASU'],
              ['Territoire', 'France — Extension possible EUIPO (marque UE)'],
            ]}
          />
          <p>Toute reproduction, représentation, utilisation ou adaptation, sous quelque forme que ce soit, de
          tout ou partie de la marque Normascan, sans l'autorisation préalable et écrite d'Evalens, est
          strictement interdite et constitue une contrefaçon passible de sanctions pénales et civiles.</p>
        </Sub>
        <Sub title="5.2 Dépôt de code source — Enveloppe Soleau">
          <DataTable
            headers={['', '']}
            rows={[
              ['Numéro Enveloppe Soleau', 'DSO2026011204'],
              ['Date de dépôt', '27 mars 2026'],
              ['Objet', "Code source de la plateforme Normascan et algorithmes d'analyse normative par IA"],
              ['Déposant', 'Marc FESSARD / EVALENS'],
            ]}
          />
          <p className="text-xs text-gray-500">L'Enveloppe Soleau constitue une preuve de date certaine pour le
          code source de Normascan, conformément à l'article L. 111-1 du Code de la Propriété Intellectuelle.</p>
        </Sub>
        <Sub title="5.3 Code source et logiciel">
          <p>Le code source de la plateforme Normascan, ses algorithmes, interfaces, extracteurs de données,
          modèles d'IA fine-tunés et toute innovation développée par Evalens sont des œuvres de l'esprit
          protégées par :</p>
          <List items={[
            "Le droit d'auteur (Code de la Propriété Intellectuelle — Articles L111-1 et suivants)",
            'Le droit des producteurs de bases de données (Articles L341-1 et suivants du CPI)',
            'Le secret des affaires (Loi n°2018-670 du 30 juillet 2018 — Articles L151-1 et suivants du Code de commerce)',
          ]} />
          <p><strong>Toute reproduction, décompilation, ingénierie inverse ou utilisation non autorisée est
          passible de 3 ans d'emprisonnement et 300 000 € d'amende</strong> (Articles L335-2 et suivants du CPI).</p>
        </Sub>
      </Article>

      <Article title="6. Données personnelles et cookies">
        <Sub title="6.1 Responsable du traitement">
          <p>Le responsable du traitement des données personnelles collectées via Normascan est EVALENS,
          représentée par Marc FESSARD, Président.<br />Contact protection des données : privacy@evalens.fr</p>
        </Sub>
        <Sub title="6.2 Données collectées">
          <p>Normascan collecte et traite les données personnelles des utilisateurs dans le cadre de la
          fourniture du service. Les traitements sont détaillés dans la <Link href="/privacy" className="underline">Politique
          de Confidentialité</Link>.</p>
        </Sub>
        <Sub title="6.3 Droits des personnes concernées">
          <p>Conformément au Règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés modifiée, vous
          disposez des droits suivants :</p>
          <List items={[
            "Droit d'accès à vos données (Art. 15 RGPD)",
            'Droit de rectification des données inexactes (Art. 16 RGPD)',
            "Droit à l'effacement (« droit à l'oubli ») (Art. 17 RGPD)",
            'Droit à la limitation du traitement (Art. 18 RGPD)',
            'Droit à la portabilité de vos données (Art. 20 RGPD)',
            "Droit d'opposition au traitement (Art. 21 RGPD)",
          ]} />
          <p>Pour exercer vos droits : privacy@evalens.fr — Délai de réponse légal : 30 jours maximum.<br />
          Droit de réclamation : vous pouvez saisir la CNIL (Commission Nationale de l'Informatique et des
          Libertés) sur cnil.fr — 3 place de Fontenoy, 75007 Paris.</p>
        </Sub>
        <Sub title="6.4 Cookies">
          <p>Normascan utilise uniquement des cookies strictement nécessaires au fonctionnement du service
          (gestion de session, préférences de langue). Aucun cookie publicitaire ou de suivi tiers n'est
          utilisé. Normascan est une plateforme B2B professionnelle sans publicité. Evalens ne vend pas et ne
          partage pas vos données à des fins publicitaires.</p>
        </Sub>
      </Article>

      <Article title="7. Limitation de responsabilité">
        <Sub title="7.1 Outil d'aide à la décision">
          <Callout tone="red">
            <p><strong>Normascan est exclusivement un outil d'aide à la décision.</strong> Les résultats fournis
            par Normascan sont produits par un modèle d'intelligence artificielle et présentés à titre indicatif
            uniquement.</p>
            <p className="mt-2">Ils ne constituent pas un avis professionnel certifié, une certification de
            conformité, ni un rapport de diagnostic réglementaire. Ils ne sauraient remplacer l'analyse et le
            jugement d'un professionnel qualifié et certifié (diagnostiqueur certifié, expert en bâtiment, maître
            d'œuvre, etc.).</p>
            <p className="mt-2"><strong>La décision finale et toute responsabilité en découlant demeurent
            exclusivement celles du professionnel utilisateur</strong>, qui doit valider les résultats au regard
            de son expertise et de sa connaissance du terrain.</p>
            <p className="mt-2 italic">Tout rapport d'analyse généré par Normascan est accompagné d'un
            avertissement légal complet relatif à l'utilisation de l'intelligence artificielle (voir <Link href="/avertissement-ia" className="underline">Annexe
            « Avertissement IA »</Link> annexée à chaque rapport). L'utilisateur s'interdit d'en supprimer,
            modifier ou tronquer le contenu.</p>
          </Callout>
        </Sub>
        <Sub title="7.2 Disponibilité du service">
          <p>Evalens s'efforce d'assurer la disponibilité continue du service Normascan, conformément aux
          engagements du <Link href="/sla" className="underline">Contrat de Maintenance et de Support (SLA)</Link>. Toutefois,
          Evalens ne saurait être tenue responsable des interruptions résultant de :</p>
          <List items={[
            "Maintenances planifiées annoncées au minimum 48h à l'avance",
            'Défaillances des prestataires tiers (Mistral AI, OVH) hors du contrôle d\'Evalens',
            'Cas de force majeure au sens de l\'article 1218 du Code civil',
            'Actions malveillantes de tiers (cyberattaques, DDoS)',
          ]} />
        </Sub>
        <Sub title="7.3 Contenu des rapports uploadés">
          <p>Les rapports de diagnostic uploadés par les utilisateurs sont traités sous leur entière
          responsabilité. Evalens n'est pas responsable du contenu des documents uploadés ni de la licéité de
          leur traitement au regard des données personnelles qu'ils peuvent contenir.</p>
        </Sub>
      </Article>

      <Article title="8. Liens hypertextes">
        <p>Le site Normascan peut contenir des liens vers des sites tiers. Evalens n'exerce aucun contrôle sur le
        contenu de ces sites et décline toute responsabilité quant aux informations, produits ou services qu'ils
        proposent.</p>
        <p>La création de liens hypertextes pointant vers Normascan est soumise à l'autorisation préalable écrite
        d'Evalens.</p>
      </Article>

      <Article title="9. Droit applicable et juridiction">
        <p><strong>Droit applicable :</strong> le présent document et l'utilisation du service Normascan sont
        soumis au droit français.</p>
        <p><strong>Juridiction compétente :</strong> en cas de litige, et à défaut de résolution amiable dans un
        délai de 30 jours, les tribunaux du ressort du siège social d'Evalens sont seuls compétents.</p>
        <p><strong>Médiation :</strong> Les parties peuvent recourir à tout moment à une médiation conventionnelle conformément aux articles 131-1 et suivants du Code de procédure civile.</p>
      </Article>

      <Article title="10. Contact">
        <DataTable
          headers={['', '']}
          rows={[
            ['Questions générales', 'contact@evalens.fr'],
            ['Support Normascan', 'support@normascan.fr'],
            ['Urgences (P1/P2)', 'urgent@normascan.fr'],
            ['RGPD / Données personnelles', 'privacy@evalens.fr'],
            ['Propriété intellectuelle', 'contact@evalens.fr — Objet : [PI]'],
            ['Signalement de vulnérabilité', 'security@normascan.fr'],
            ['Adresse postale', 'EVALENS SASU – 58 rue de Monceau CS 48756 75380 Paris Cedex 08'],
          ]}
        />
      </Article>

      <p className="text-xs text-gray-400 text-center mt-6">EVALENS SASU — Normascan — © 2026 Tous droits réservés</p>
    </>
  );
}

// ── SLA ──────────────────────────────────────────────────────────────────

function SLAContent() {
  return (
    <>
      <Callout tone="blue">
        <p>Ce contrat définit les engagements d'Evalens SASU envers ses clients en matière de disponibilité, de
        réactivité et de qualité de service pour la plateforme Normascan. Il constitue une annexe aux Conditions
        Générales de Vente (<Link href="/cgv" className="underline">CGV</Link>) et s'applique à tous les tiers de
        licence actifs (Essentiel, Pro, Cabinet, Entreprise, Grand compte).</p>
        <p className="mt-2">La présente version 1.4 s'applique à compter du 24/10/2026. Jusqu'au 23/10/2026
        inclus, la version 1.3 du 24/09/2026 demeure applicable : disponibilité garantie de 99,5 % pour les
        abonnements Essentiel, Pro et Cabinet, et de 99,9 % pour les licences Entreprise et Grand Compte.</p>
      </Callout>

      <Article n={1} title="Parties et objet">
        <p><strong>Prestataire :</strong> EVALENS, SASU au capital de 5 000 €, dont le siège social est situé 58
        rue de Monceau CS 48756 75380 Paris Cedex 08, immatriculée au RCS de PARIS sous le numéro 103593489,
        ci-après « Evalens ».</p>
        <p><strong>Client :</strong> toute personne morale ou physique ayant souscrit à une licence Normascan,
        ci-après « le Client ».</p>
        <p><strong>Objet :</strong> le présent SLA définit les niveaux de service garantis pour la plateforme
        Normascan accessible à l'adresse https://app.normascan.fr et ses fonctionnalités.</p>
      </Article>

      <Article n={2} title="Définitions">
        <DataTable
          headers={['Terme', 'Définition']}
          rows={[
            ['Disponibilité', 'Capacité de la plateforme Normascan à répondre aux requêtes des utilisateurs finaux, mesurée sur un mois calendaire.'],
            ['Indisponibilité', 'Un service est considéré indisponible s\'il retourne une erreur HTTP 5xx ou un timeout > 30s. Exclus : latence dégradée (ex : temps de réponse > 2s) ou fonctionnalités secondaires.'],
            ['Incident', 'Toute interruption non planifiée ou dégradation significative de performance affectant le service Normascan.'],
            ['Temps de réponse', "Délai entre la réception d'un signalement d'incident par Evalens et l'envoi d'un premier accusé de réception au Client."],
            ['Temps de résolution', "Délai entre la réception d'un signalement d'incident et le rétablissement complet du service affecté."],
            ['Plage de service', "Période pendant laquelle les engagements de disponibilité s'appliquent : 7j/7, 24h/24 pour le service d'analyse automatique."],
            ['Analyse LLM', "Traitement d'un rapport amiante par Normascan via le modèle d'IA Mistral Large."],
            ['OCR', "Conversion d'un PDF scanné en texte par Mistral OCR (service tiers)."],
            ['Maintenance planifiée', 'Opération de mise à jour ou de maintenance annoncée au minimum 48h à l\'avance.'],
          ]}
        />
      </Article>

      <Article n={3} title="Niveaux de service (SLA par tier)">
        <Sub title="3.1 Taux de disponibilité garantis">
          <DataTable
            headers={['Tier de licence', 'SLA uptime', 'Indispo max/mois', 'Indispo max/an', 'Crédit si dépassé']}
            rows={[
              ['Essentiel', '99 %', '7h 18min', '87h 36min', 'Best effort'],
              ['Pro', '99 %', '7h 18min', '87h 36min', '2 % de la mensualité (plafond 10 %)'],
              ['Cabinet', '99 %', '7h 18min', '87h 36min', '2 % de la mensualité (plafond 10 %)'],
              ['Entreprise', '99,5 %', '3h 39min', '43h 48min', '5 % de la mensualité (plafond 30 %)'],
              ['Grand Compte', '99,5 %', '3h 39min', '43h 48min', 'Négocié'],
            ]}
          />
          <p className="text-xs text-gray-500"><strong>Mesure :</strong> temps réel via UptimeRobot.
          <strong> Exclusions :</strong> tiers (Mistral/OVH), maintenances planifiées, force majeure, mauvaise
          utilisation. La disponibilité est mesurée sur une base mensuelle moyenne de 730 heures ; les maintenances planifiées annoncées
          ≥ 48h à l'avance ne sont pas comptabilisées dans le calcul du taux d'indisponibilité.</p>
        </Sub>
        <Sub title="3.2 Temps de réponse aux incidents">
          <DataTable
            headers={['Gravité', 'Description', 'Essentiel / Pro / Cabinet', 'Entreprise/Grand compte']}
            rows={[
              ['P1 — Critique', 'Service indisponible', '4h*', '2h*'],
              ['P2 — Majeur', 'Fonctionnalité majeure hors service', '8h*', '4h*'],
              ['P3 — Mineur', 'Bug mineur', '48h*', '24h*'],
            ]}
          />
          <p className="text-xs text-gray-500">* Uniquement les jours ouvrés : du lundi au vendredi, de 9h00-18h00 (heure de Paris), hors jours fériés français. 
          Canal de signalement incident : urgent@normascan.fr (P1/P2),
          support@normascan.fr (P3/autres).</p>
        </Sub>
      </Article>

      <Article n={4} title="Exclusions de la garantie de disponibilité">
        <p>Les événements suivants ne sont pas comptabilisés dans le calcul du taux d'indisponibilité :</p>
        <List items={[
          "Maintenances planifiées annoncées au minimum 48h à l'avance par email ou notification in-app",
          "Incidents liés aux services de tiers sur lesquels Evalens n'a pas de contrôle direct : Mistral AI (API LLM et OCR), OVH (hébergement infrastructure), services réseau intermédiaires (FAI du Client)",
          'Attaques DDoS ou incidents de sécurité externes malgré les mesures de protection en place',
          "Cas de force majeure au sens de l'article 1218 du Code civil",
          "Actions ou omissions du Client : utilisation non conforme du service, dépassement volontaire des limites contractuelles",
          "Environnements de développement ou de staging (seul l'environnement de production https://app.normascan.fr est couvert)",
        ]} />
        <p><strong>Sous-traitants technologiques :</strong> Evalens utilise Mistral AI SAS (Paris, France) pour le
        traitement par IA des rapports et OVH SAS (Roubaix, France) pour l'hébergement. La disponibilité de ces
        services tiers conditionne partiellement celle de Normascan. Evalens met en œuvre ses meilleurs efforts
        pour maintenir des alternatives en cas de défaillance.</p>
      </Article>

      <Article n={5} title="Mesure et reporting">
        <Sub title="5.1 Mesure de la disponibilité">
          <p>La disponibilité de Normascan est mesurée via UptimeRobot (sonde HTTP toutes les 5 min) disponible à
          l'adresse : https://stats.uptimerobot.com/D7vgYMrK9f.</p>
          <p><strong>Définition de l'indisponibilité :</strong> un service est considéré indisponible s'il
          retourne une erreur HTTP 5xx ou un timeout &gt; 30s. Exclusions : latence dégradée (ex : temps de
          réponse &gt; 2s) ou fonctionnalités secondaires.</p>
        </Sub>
        <Sub title="5.2 Rapport annuel">
          <p>Pour les tiers Entreprise et Grand Compte, Evalens fournit un rapport annuel de disponibilité
          comprenant :</p>
          <List items={[
            'Taux de disponibilité mesuré',
            'Nombre et durée des incidents survenus, avec classification de gravité',
            'Détail des maintenances réalisées',
            'Éventuel crédit SLA calculé automatiquement',
          ]} />
        </Sub>
      </Article>

      <Article n={6} title="Crédit SLA">
        <Sub title="6.1 Processus de demande de crédit">
          <p>Le client doit signaler l'incident sous 72h via support@normascan.fr avec :</p>
          <List items={[
            "Date et heure de début/fin de l'indisponibilité",
            "Preuve : capture d'écran ou log (ex : erreur HTTP 5xx, timeout)",
          ]} />
          <p>Evalens valide la demande sous 15 jours et applique le crédit sur la prochaine facture.</p>
        </Sub>
        <Sub title="6.2 Application du crédit SLA">
          <p>Si le taux de disponibilité mensuel mesuré est inférieur au SLA garanti pour le tier du client, un
          crédit est appliqué automatiquement sur la facture du mois suivant :</p>
          <DataTable
            headers={['Tier', 'Seuil mensuel dépassé', 'Crédit accordé', 'Plafond mensuel']}
            rows={[
              ['Pro', '> 7h 18min', '2 % de la mensualité', '10 % de la mensualité'],
              ['Cabinet', '> 7h 18min', '2 % de la mensualité', '10 % de la mensualité'],
              ['Entreprise', '> 3h 39min', '5 % de la mensualité', '30 % de la mensualité'],
              ['Grand compte', '> 3h 39min', 'Négocié', 'Négocié'],
            ]}
          />
          <p className="text-xs text-gray-500"><strong>Conditions :</strong> les crédits sont appliqués
          uniquement pour les incidents validés par Evalens après vérification des preuves fournies.
          <strong> Exclusions :</strong> les crédits ne s'appliquent pas aux indisponibilités liées aux exclusions
          du SLA (Article 4) et au tier non concerné (tier Essentiel, en best effort). <strong>Recours unique :</strong> le crédit SLA
          est le seul recours en cas de non-respect du SLA — il ne donne pas droit à des dommages et intérêts
          supplémentaires ni à une résiliation pour motif légitime, sauf dépassement répété sur 3 mois
          consécutifs.</p>
        </Sub>
      </Article>

      <Article n={7} title="Infrastructure et sécurité">
        <Sub title="7.1 Architecture technique">
          <p>Normascan est hébergé sur des serveurs OVH SAS situés en France (Union Européenne). L'infrastructure
          comprend :</p>
          <List items={[
            'Serveur VPS dédié avec 6 vCores, 12 Go RAM, 100 Go stockage SSD',
            'Chiffrement SSL/TLS (HTTPS) obligatoire pour toutes les communications',
            "Accès sécurisé par JWT (JSON Web Tokens) avec expiration",
            'Rate limiting nginx pour prévenir les abus',
            'Sauvegardes automatiques quotidiennes de la base de données',
            'Filtrage et validation de tous les fichiers uploadés',
          ]} />
        </Sub>
        <Sub title="7.2 Sécurité des données">
          <List items={[
            "Les rapports amiante uploadés sont stockés dans l'espace de stockage sécurisé du serveur OVH",
            "L'accès aux données est strictement limité aux utilisateurs autorisés du compte client",
            'Les données transmises à l\'API Mistral AI sont couvertes par les garanties contractuelles Mistral AI (société française, conforme RGPD)',
            'Les données ne sont jamais transmises à des tiers non mentionnés dans la Politique de Confidentialité',
            'Les sauvegardes sont conservées 30 jours',
          ]} />
        </Sub>
      </Article>

      <Article n={8} title="Procédure de signalement d'incident">
        <DataTable
          headers={['Priorité', 'Canal', 'Informations à fournir']}
          rows={[
            ['P1 Critique', 'urgent@normascan.fr', "Description, capture d'écran, heure de début, analyses impactées"],
            ['P2 Majeur', 'support@normascan.fr', 'Description, étapes de reproduction, ID analyse concernée'],
            ['P3/P4', 'support@normascan.fr', 'Description de l\'anomalie ou de la demande'],
          ]}
        />
        <p className="text-xs text-gray-500">Objet du mail : préciser systématiquement [P1/P2/P3/P4] + nom de
        l'organisation + description courte. Exemple : « [P2] Bureau MOA Lyon – Export Excel bloqué depuis 14h ».</p>
      </Article>

      <Article n={9} title="Maintenance évolutive et planifiée">
        <Sub title="9.1 Maintenances planifiées">
          <p>Evalens effectue des maintenances régulières pour déployer des mises à jour, corrections de sécurité
          et nouvelles fonctionnalités. Ces maintenances sont planifiées de préférence en dehors des heures de
          bureau (avant 8h ou après 20h, heure de Paris), le week-end pour les mises à jour majeures, avec un
          préavis minimum de 48h par email et notification in-app. Durée typique : &lt; 30 minutes.</p>
        </Sub>
        <Sub title="9.2 Mises à jour de la plateforme">
          <p>Les mises à jour fonctionnelles sont notifiées aux Clients par email de release notes (mises à jour
          majeures), changelog accessible dans l'interface Normascan, et communication directe du CSM pour les
          clients Entreprise et Grand Compte.</p>
          <p><strong>Évolutions normatives :</strong> les évolutions des normes (ex : révision NF X46-020) sont
          intégrées dans les meilleurs délais compatibles avec la qualité de service. Les clients sont informés
          au minimum 30 jours avant l'entrée en vigueur d'une évolution normative impactant les résultats
          d'analyse.</p>
        </Sub>
      </Article>

      <Article n={10} title="Formation et onboarding">
        <DataTable
          headers={['Tier', 'Onboarding et formation inclus']}
          rows={[
            ['Essentiel / Pro / Cabinet', 'Documentation en ligne + FAQ'],
            ['Entreprise', 'Documentation + 1 session de formation en ligne ou en présentiel (1h)'],
            ['Grand Compte', 'Formation équipe complète + accompagnement continu'],
          ]}
        />
      </Article>

      <Article n={11} title="Dispositions générales">
        <Sub title="11.1 Durée et révision">
          <p>Le présent SLA entre en vigueur à la date de souscription du Client et demeure applicable pendant
          toute la durée du contrat de licence. Evalens se réserve le droit de réviser ce SLA avec un préavis de
          30 jours. En cas de révision défavorable au Client, celui-ci dispose d'un droit de résiliation sans
          pénalité dans les 30 jours suivant la notification.</p>
        </Sub>
        <Sub title="11.2 Limitation de responsabilité">
          <p>La responsabilité d'Evalens au titre du présent SLA est limitée au montant des crédits SLA calculés
          conformément à l'article 6. En aucun cas Evalens ne peut être tenu responsable de pertes indirectes,
          manque à gagner ou dommages consécutifs résultant d'une interruption de service.</p>
        </Sub>
        <Sub title="11.3 Droit applicable et juridiction">
          <p>Le présent SLA est soumis au droit français. En cas de litige, les parties s'engagent à rechercher
          une solution amiable. À défaut, le Tribunal de Commerce compétent du ressort du siège social d'Evalens
          sera seul compétent.</p>
        </Sub>
      </Article>
    </>
  );
}

const CONTENT_COMPONENTS = {
  cgv: CGVContent,
  'avertissement-ia': AvertissementIAContent,
  privacy: PrivacyContent,
  mentions: MentionsContent,
  sla: SLAContent,
};

// La page CGV : quelle version afficher, son en-tête, le bandeau de préavis.
// Composant à part parce que le calendrier est un hook : les quatre autres
// documents n'ont qu'une version et restent rendus comme avant.
function CorpsCgv({ meta }) {
  const calendrier = useCalendrierCgv();
  const demandee = new URLSearchParams(window.location.search).get('version');
  const demandeeEnLigne = CGV_VERSIONS.find(v => v.numero === demandee);
  const affichee = demandeeEnLigne
    || CGV_VERSIONS.find(v => v.numero === calendrier.en_vigueur);
  const Texte = affichee.Texte;
  const libelleDate = affichee.numero === calendrier.a_venir
    ? `Applicable à partir du ${dateFr(affichee.en_vigueur_le)}`
    : `Entrée en vigueur : ${dateFr(affichee.en_vigueur_le)}`;
  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{meta.title}</h1>
      <p className="text-xs text-gray-500 mb-6">EVALENS — Normascan · Version {affichee.numero} · {libelleDate}</p>
      {demandee && !demandeeEnLigne
        ? <BandeauVersionIndisponible numero={demandee} calendrier={calendrier} />
        : <BandeauVersionCgv affichee={affichee} calendrier={calendrier} />}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <Texte />
      </div>
      <ArchiveCgv calendrier={calendrier} />
    </>
  );
}

const LegalPage = ({ type }) => {
  const meta = PAGES_META[type];
  const ContentComponent = CONTENT_COMPONENTS[type];

  if (!meta || !ContentComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Page légale introuvable.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <EnTeteDocument />

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        {type === 'cgv' ? <CorpsCgv meta={meta} /> : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{meta.title}</h1>
            <p className="text-xs text-gray-500 mb-6">EVALENS — Normascan · {meta.version} · {meta.date}</p>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <ContentComponent />
            </div>
          </>
        )}

        <div className="mt-6 text-sm text-gray-500 flex flex-wrap gap-4">
          {CROSS_LINKS.map(link => (
            <Link key={link.href} href={link.href} className="hover:underline">{link.label}</Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default LegalPage;
